import { Head, Link } from '@inertiajs/react';
// lucide-react dropped its brand icons; `@/components/icons` carries the same paths.
import { Github } from '@/components/icons';
import type { ArcadeGamesShow } from '@/types';
import { ArcadeVoteButton } from './components/arcade-vote-button';
import { GamePlayer } from './components/game-player';
import { VersionSelector } from './components/version-selector';

export default function Show({
  game,
  versions,
  challengeId,
  embedUrl,
  playCount,
  voteCount,
  hasVoted,
  votingOpen,
  authenticated,
  hasGoogleAccount,
}: ArcadeGamesShow) {
  const title = `Platanus Hack 26 Arcade: ${game.title}`;
  const description =
    game.description || `Play ${game.title} by ${game.githubUsername}`;
  const versionSummaries = versions.map((candidate) => ({
    id: candidate.id,
    slug: candidate.slug,
    versionNumber: candidate.versionNumber,
    compressedSizeKB: (candidate.codeSize / 1024).toFixed(2),
    uploadedAt: candidate.uploadedAt,
  }));

  const versionSelector = (
    <VersionSelector
      gameSlug={game.slug}
      versions={versionSummaries}
      currentVersionId={game.versionId}
      latestVersionId={game.latestVersionId}
      isLatest={game.isLatest}
      currentVersionNumber={game.versionNumber}
    />
  );

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {game.coverUrl && <meta property="og:image" content={game.coverUrl} />}
        <meta
          name="twitter:card"
          content={game.coverUrl ? 'summary_large_image' : 'summary'}
        />
      </Head>
      {/* app/(public)/26/arcade/layout.tsx wraps this whole tree in a black page. */}
      <div className="min-h-screen bg-black">
      <div className="flex h-[100svh] flex-col overflow-hidden font-mono md:h-screen">
        <header className="shrink-0 border-white/[0.06] border-b px-3 py-2 md:px-6 md:py-4">
          <div className="mx-auto flex max-w-[1400px] items-center gap-3 md:gap-5">
            {game.coverUrl && (
              <img
                src={game.coverUrl}
                alt={game.title}
                width={106}
                height={80}
                loading="lazy"
                className="hidden h-20 w-[106px] shrink-0 rounded-lg border border-white/10 object-cover shadow-lg md:block"
              />
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1 md:gap-1.5">
              <Link
                href="/26/arcade"
                className="text-[9px] text-white/60 uppercase tracking-[0.2em] transition-opacity hover:text-primary md:text-[10px] md:tracking-[0.3em]"
              >
                Platanus Hack 26 Arcade
              </Link>
              <div className="flex items-center gap-2 md:gap-3">
                <h1 className="crt-glow truncate font-bold text-base text-primary md:text-2xl">
                  {game.title}
                </h1>
                <div className="hidden md:block">{versionSelector}</div>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <a
                  href={game.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[11px] text-white/60 transition-colors hover:text-primary md:gap-1.5 md:text-xs"
                >
                  <Github className="h-3 w-3 md:h-3.5 md:w-3.5" />
                  <span className="hidden md:inline">{game.githubUsername}</span>
                </a>
                <div className="md:hidden">{versionSelector}</div>
                {game.description && (
                  <p className="hidden truncate text-white/50 text-xs md:block">
                    {game.description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center">
              <ArcadeVoteButton
                gameId={game.id}
                gameSlug={game.slug}
                gameTitle={game.title}
                voted={hasVoted}
                count={voteCount}
                isAuthenticated={authenticated}
                hasGoogleAccount={hasGoogleAccount}
                votingOpen={votingOpen}
              />
            </div>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 items-center justify-center px-2 pb-2">
          <GamePlayer
            challengeId={challengeId}
            embedUrl={embedUrl}
            title={game.title}
            gameId={game.id}
            initialPlayCount={playCount}
            playerMode={game.playerMode}
            arcadeMapping={game.arcadeMapping}
          />
        </div>
      </div>
      </div>
    </>
  );
}
